import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import TrustBadgeOrg from '../components/TrustBadgeOrg';
import { useOrg } from '../context/OrgContext';

function PrivacyCenter() {
  const navigate = useNavigate();
  const params = useParams();
  const { orgSlug } = useOrg();
  const slug = params.slug || orgSlug;
  const dashboardPath = slug ? `/org/${slug}/dashboard` : '/dashboard';
  const [orgId, setOrgId] = useState(null);
  const [privacyInfo, setPrivacyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [optOutLoading, setOptOutLoading] = useState(false);
  const [optOutSuccess, setOptOutSuccess] = useState(false);

  useEffect(() => {
    fetchOrgAndPrivacy();
  }, []);

  const fetchOrgAndPrivacy = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const headers = { Authorization: `Bearer ${token}` };

      const statusRes = await axios.get('/api/org/onboard/status', { headers });
      const memberships = statusRes.data.memberships || [];

      if (memberships.length > 0) {
        const id = memberships[0].org_id;
        setOrgId(id);

        try {
          const privacyRes = await axios.get('/api/org/privacy', { headers });
          setPrivacyInfo(privacyRes.data);
        } catch (err) {
          // Privacy endpoint may not return data if user has no org — that's ok
          console.error('Failed to fetch privacy info:', err);
        }
      } else {
        setError('You are not currently associated with any organization.');
      }
    } catch (err) {
      setError('Failed to load privacy information.');
    } finally {
      setLoading(false);
    }
  };

  const handleOptOut = async () => {
    setOptOutLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(
        '/api/org/privacy/opt-out',
        { orgId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOptOutSuccess(true);
      setShowConfirm(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to opt out. Please try again.');
    } finally {
      setOptOutLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <p style={styles.loadingText}>Loading privacy center...</p>
      </div>
    );
  }

  if (optOutSuccess) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.successCard}>
          <div style={styles.successIcon}>✅</div>
          <h2 style={styles.successTitle}>You have left the organization</h2>
          <p style={styles.successText}>
            Your public community access is preserved. You can continue using Belong's public communities and all your existing connections remain intact.
          </p>
          <button onClick={() => navigate('/dashboard')} style={styles.primaryBtn}>
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (error && !orgId) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.errorCard}>
          <p style={styles.errorText}>{error}</p>
          <button onClick={() => navigate(dashboardPath)} style={styles.primaryBtn}>
            ← Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const kThreshold = privacyInfo?.kAnonymityThreshold ?? 10;

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        {/* Trust Badge */}
        <div style={styles.trustBadgeWrapper}>
          <TrustBadgeOrg />
        </div>

        <h1 style={styles.title}>🛡️ Privacy Center</h1>
        <p style={styles.subtitle}>
          Understand how your data is protected within your organization's Belong instance.
        </p>

        {error && <div style={styles.errorBanner}>{error}</div>}

        {/* What your employer CAN see */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>
            <span style={styles.sectionIcon}>👁️</span> What your employer CAN see
          </h2>
          <div style={styles.card}>
            <ul style={styles.list}>
              <li style={styles.listItem}>
                <span style={styles.checkIcon}>✅</span>
                Aggregated participation counts (e.g., "42 employees are active this month")
              </li>
              <li style={styles.listItem}>
                <span style={styles.checkIcon}>✅</span>
                Overall community engagement rates across the organization
              </li>
              <li style={styles.listItem}>
                <span style={styles.checkIcon}>✅</span>
                Aggregated sentiment trends by topic category (never by individual)
              </li>
              <li style={styles.listItem}>
                <span style={styles.checkIcon}>✅</span>
                Topic activity heatmap showing which categories are most active
              </li>
            </ul>
            <p style={styles.cardNote}>
              All metrics are aggregated across at least {kThreshold} participants before being shown.
            </p>
          </div>
        </section>

        {/* What your employer CANNOT see */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>
            <span style={styles.sectionIcon}>🚫</span> What your employer CANNOT see
          </h2>
          <div style={{ ...styles.card, borderColor: '#fca5a5' }}>
            <ul style={styles.list}>
              <li style={styles.listItem}>
                <span style={styles.xIcon}>❌</span>
                Your individual posts, comments, or reactions
              </li>
              <li style={styles.listItem}>
                <span style={styles.xIcon}>❌</span>
                Which identity communities you belong to
              </li>
              <li style={styles.listItem}>
                <span style={styles.xIcon}>❌</span>
                Your activity timestamps or browsing patterns
              </li>
              <li style={styles.listItem}>
                <span style={styles.xIcon}>❌</span>
                Your connections, messages, or profile information
              </li>
              <li style={styles.listItem}>
                <span style={styles.xIcon}>❌</span>
                Your full email address (only the domain is stored)
              </li>
            </ul>
          </div>
        </section>

        {/* K-Anonymity Explanation */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>
            <span style={styles.sectionIcon}>🔒</span> How we protect your privacy
          </h2>
          <div style={styles.kAnonCard}>
            <h3 style={styles.kAnonTitle}>K-Anonymity Protection (Threshold: {kThreshold})</h3>
            <p style={styles.kAnonText}>
              We use a privacy technique called <strong>k-anonymity</strong> to ensure your data is never identifiable. 
              Here's what that means in plain language:
            </p>
            <ul style={styles.list}>
              <li style={styles.listItem}>
                Any metric shown to your employer must include data from at least <strong>{kThreshold} different people</strong>.
              </li>
              <li style={styles.listItem}>
                If fewer than {kThreshold} people contribute to a metric, it is completely hidden — your employer sees nothing.
              </li>
              <li style={styles.listItem}>
                This means your individual activity can never be singled out or inferred from the data your employer sees.
              </li>
            </ul>
            <p style={styles.kAnonText}>
              This protection is enforced at the database level — it cannot be bypassed by anyone, including Belong administrators.
            </p>
          </div>
        </section>

        {/* Opt-Out */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>
            <span style={styles.sectionIcon}>🚪</span> Leave Organization
          </h2>
          <div style={styles.card}>
            <p style={styles.optOutText}>
              You can leave your organization's Belong instance at any time. This will:
            </p>
            <ul style={styles.list}>
              <li style={styles.listItem}>Remove your access to organization-scoped communities</li>
              <li style={styles.listItem}>Exclude your data from future aggregated metrics</li>
              <li style={styles.listItem}>
                <strong>Preserve</strong> your access to all public communities and existing connections
              </li>
            </ul>

            {!showConfirm ? (
              <button onClick={() => setShowConfirm(true)} style={styles.optOutBtn}>
                Leave Organization
              </button>
            ) : (
              <div style={styles.confirmBox}>
                <p style={styles.confirmText}>
                  Are you sure you want to leave your organization? This action cannot be undone.
                </p>
                <div style={styles.confirmActions}>
                  <button
                    onClick={handleOptOut}
                    disabled={optOutLoading}
                    style={styles.confirmYesBtn}
                  >
                    {optOutLoading ? 'Leaving...' : 'Yes, Leave Organization'}
                  </button>
                  <button onClick={() => setShowConfirm(false)} style={styles.confirmNoBtn}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

        <div style={styles.footer}>
          <button onClick={() => navigate(dashboardPath)} style={styles.backLink}>
            ← Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f3f4f6',
    padding: '32px 16px',
  },
  content: {
    maxWidth: '700px',
    margin: '0 auto',
  },
  trustBadgeWrapper: {
    marginBottom: '24px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1f2937',
    margin: '0 0 8px 0',
  },
  subtitle: {
    fontSize: '15px',
    color: '#6b7280',
    margin: '0 0 32px 0',
  },
  section: {
    marginBottom: '28px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1f2937',
    margin: '0 0 12px 0',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  sectionIcon: {
    fontSize: '20px',
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '20px 24px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    border: '1px solid #e5e7eb',
  },
  cardNote: {
    fontSize: '12px',
    color: '#6b7280',
    marginTop: '12px',
    fontStyle: 'italic',
  },
  list: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  listItem: {
    fontSize: '14px',
    color: '#374151',
    padding: '6px 0',
    lineHeight: 1.5,
  },
  checkIcon: {
    marginRight: '8px',
  },
  xIcon: {
    marginRight: '8px',
  },
  kAnonCard: {
    backgroundColor: '#f5f3ff',
    borderRadius: '12px',
    padding: '24px',
    border: '1px solid #ddd6fe',
  },
  kAnonTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#4C1D95',
    margin: '0 0 12px 0',
  },
  kAnonText: {
    fontSize: '14px',
    color: '#374151',
    lineHeight: 1.6,
    margin: '0 0 12px 0',
  },
  optOutText: {
    fontSize: '14px',
    color: '#374151',
    margin: '0 0 12px 0',
  },
  optOutBtn: {
    marginTop: '16px',
    padding: '10px 24px',
    backgroundColor: '#dc2626',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  confirmBox: {
    marginTop: '16px',
    backgroundColor: '#fef2f2',
    borderRadius: '8px',
    padding: '16px',
    border: '1px solid #fecaca',
  },
  confirmText: {
    fontSize: '14px',
    color: '#991b1b',
    margin: '0 0 12px 0',
  },
  confirmActions: {
    display: 'flex',
    gap: '12px',
  },
  confirmYesBtn: {
    padding: '8px 20px',
    backgroundColor: '#dc2626',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  confirmNoBtn: {
    padding: '8px 20px',
    backgroundColor: '#fff',
    color: '#374151',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  footer: {
    marginTop: '32px',
    paddingTop: '16px',
    borderTop: '1px solid #e5e7eb',
  },
  backLink: {
    background: 'none',
    border: 'none',
    color: '#7C3AED',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    padding: 0,
  },
  loadingContainer: {
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f3f4f6',
  },
  loadingText: {
    fontSize: '16px',
    color: '#6b7280',
  },
  errorCard: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '32px',
    textAlign: 'center',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
  },
  errorText: {
    fontSize: '15px',
    color: '#dc2626',
    marginBottom: '16px',
  },
  errorBanner: {
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    padding: '12px 16px',
    borderRadius: '8px',
    fontSize: '14px',
    marginBottom: '20px',
  },
  primaryBtn: {
    padding: '10px 20px',
    backgroundColor: '#7C3AED',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  successCard: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '48px',
    maxWidth: '500px',
    width: '100%',
    textAlign: 'center',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
  },
  successIcon: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  successTitle: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#1f2937',
    margin: '0 0 12px 0',
  },
  successText: {
    fontSize: '14px',
    color: '#6b7280',
    margin: '0 0 24px 0',
    lineHeight: 1.6,
  },
};

export default PrivacyCenter;
